import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import Layout from "@/components/Layout";
import Modal from "@/components/Modal";
import { euro, supplierLabel } from "@/lib/format";
import { toast } from "sonner";
import { IDS } from "@/constants/testIds";

const emptyP = { code: "", name: "", category: "", supplier_id: "", sizes: "", colors: "", cost: 0, price: 0, description: "" };
const emptyS = { name: "", contact: "", phone: "", email: "", web: "", note: "" };

export default function Anagrafiche() {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [pModal, setPModal] = useState(false);
  const [sModal, setSModal] = useState(false);
  const [pForm, setPForm] = useState(emptyP);
  const [sForm, setSForm] = useState(emptyS);
  const [pEditing, setPEditing] = useState(null);
  const [sEditing, setSEditing] = useState(null);

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/products"), api.get("/suppliers")]);
    setProducts(p.data); setSuppliers(s.data);
  };
  useEffect(() => { load(); }, []);

  const openNewProduct = () => { setPForm(emptyP); setPEditing(null); setPModal(true); };
  const openEditProduct = (p) => { setPForm({ ...emptyP, ...p }); setPEditing(p.id); setPModal(true); };
  const saveProduct = async () => {
    if (!pForm.code.trim() || !pForm.name.trim()) return toast.error("Compila codice e nome");
    try {
      const body = { ...pForm, cost: +pForm.cost || 0, price: +pForm.price || 0 };
      if (pEditing) await api.put(`/products/${pEditing}`, body);
      else await api.post("/products", body);
      toast.success("Prodotto salvato");
      setPModal(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const delProduct = async (id) => {
    if (!window.confirm("Eliminare il prodotto e le relative varianti?")) return;
    await api.delete(`/products/${id}`); toast.success("Eliminato"); load();
  };

  const openNewSupplier = () => { setSForm(emptyS); setSEditing(null); setSModal(true); };
  const openEditSupplier = (s) => { setSForm({ ...emptyS, ...s }); setSEditing(s.id); setSModal(true); };
  const saveSupplier = async () => {
    if (!sForm.name.trim()) return toast.error("Inserisci la ragione sociale");
    try {
      if (sEditing) await api.put(`/suppliers/${sEditing}`, sForm);
      else await api.post("/suppliers", sForm);
      toast.success("Fornitore salvato");
      setSModal(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const delSupplier = async (id) => {
    if (!window.confirm("Eliminare il fornitore?")) return;
    await api.delete(`/suppliers/${id}`); toast.success("Eliminato"); load();
  };

  return (
    <Layout topbarActions={
      <button className="btn accent" onClick={openNewProduct} data-testid={IDS.newProductBtn}>
        <i className="fa-solid fa-plus" /> Nuovo prodotto
      </button>
    }>
      <div className="section-title">
        <div><h2>Anagrafiche</h2><p>Archivio dinamico di prodotti, modelli e fornitori.</p></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h3>Prodotti e modelli</h3>
          <button className="btn primary" onClick={openNewProduct}><i className="fa-solid fa-plus" /> Aggiungi</button>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Codice</th><th>Prodotto</th><th>Categoria</th><th>Fornitore</th><th>Taglie</th><th>Costo</th><th>Prezzo</th><th></th></tr></thead>
            <tbody data-testid="products-table-body">
              {products.length ? products.map((p) => (
                <tr key={p.id}>
                  <td><b>{p.code}</b></td>
                  <td>{p.name}<br /><small style={{ color: "var(--muted)" }}>{p.description || ""}</small></td>
                  <td>{p.category || "—"}</td>
                  <td>{supplierLabel(suppliers, p.supplier_id)}</td>
                  <td>{p.sizes || "—"}</td>
                  <td>{euro(p.cost)}</td>
                  <td>{euro(p.price)}</td>
                  <td style={{ display: "flex", gap: 6 }}>
                    <button className="btn small" onClick={() => openEditProduct(p)}><i className="fa-solid fa-pen" /></button>
                    <button className="btn small danger" onClick={() => delProduct(p.id)}><i className="fa-solid fa-trash" /></button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={8} className="empty">Nessun prodotto inserito.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h3>Fornitori</h3>
          <button className="btn" onClick={openNewSupplier} data-testid={IDS.newSupplierBtn}><i className="fa-solid fa-plus" /> Aggiungi</button>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Ragione sociale</th><th>Referente</th><th>Email</th><th>Telefono</th><th></th></tr></thead>
            <tbody data-testid="suppliers-table-body">
              {suppliers.length ? suppliers.map((s) => (
                <tr key={s.id}>
                  <td><b>{s.name}</b></td>
                  <td>{s.contact || "—"}</td>
                  <td>{s.email || "—"}</td>
                  <td>{s.phone || "—"}</td>
                  <td style={{ display: "flex", gap: 6 }}>
                    <button className="btn small" onClick={() => openEditSupplier(s)}><i className="fa-solid fa-pen" /></button>
                    <button className="btn small danger" onClick={() => delSupplier(s.id)}><i className="fa-solid fa-trash" /></button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={5} className="empty">Nessun fornitore registrato.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={pModal} title={pEditing ? "Modifica prodotto" : "Nuovo prodotto / modello"} onClose={() => setPModal(false)} onSave={saveProduct}>
        <div className="form-grid">
          <div className="field"><label>Codice prodotto *</label><input value={pForm.code} onChange={(e) => setPForm({ ...pForm, code: e.target.value })} data-testid="f-code" /></div>
          <div className="field"><label>Nome modello *</label><input value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} data-testid="f-name" /></div>
          <div className="field"><label>Categoria</label><input value={pForm.category} onChange={(e) => setPForm({ ...pForm, category: e.target.value })} placeholder="Body, tuta, accessorio..." /></div>
          <div className="field"><label>Fornitore</label>
            <select value={pForm.supplier_id} onChange={(e) => setPForm({ ...pForm, supplier_id: e.target.value })}>
              <option value="">Nessuno</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Taglie disponibili</label><input value={pForm.sizes} onChange={(e) => setPForm({ ...pForm, sizes: e.target.value })} placeholder="es. 4, 6, 8, 10, XS, S, M" /></div>
          <div className="field"><label>Colori disponibili</label><input value={pForm.colors} onChange={(e) => setPForm({ ...pForm, colors: e.target.value })} placeholder="es. Nero, Rosa, Blu" /></div>
          <div className="field"><label>Costo unitario</label><input type="number" step="0.01" min="0" value={pForm.cost} onChange={(e) => setPForm({ ...pForm, cost: e.target.value })} /></div>
          <div className="field"><label>Prezzo vendita</label><input type="number" step="0.01" min="0" value={pForm.price} onChange={(e) => setPForm({ ...pForm, price: e.target.value })} /></div>
          <div className="field full"><label>Descrizione / note</label><textarea rows="3" value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} /></div>
        </div>
      </Modal>

      <Modal open={sModal} title={sEditing ? "Modifica fornitore" : "Nuovo fornitore"} onClose={() => setSModal(false)} onSave={saveSupplier}>
        <div className="form-grid">
          <div className="field full"><label>Ragione sociale *</label><input value={sForm.name} onChange={(e) => setSForm({ ...sForm, name: e.target.value })} data-testid="s-name" /></div>
          <div className="field"><label>Referente</label><input value={sForm.contact} onChange={(e) => setSForm({ ...sForm, contact: e.target.value })} /></div>
          <div className="field"><label>Telefono</label><input value={sForm.phone} onChange={(e) => setSForm({ ...sForm, phone: e.target.value })} /></div>
          <div className="field"><label>Email</label><input type="email" value={sForm.email} onChange={(e) => setSForm({ ...sForm, email: e.target.value })} /></div>
          <div className="field"><label>Sito web</label><input value={sForm.web} onChange={(e) => setSForm({ ...sForm, web: e.target.value })} /></div>
          <div className="field full"><label>Note</label><textarea rows="3" value={sForm.note} onChange={(e) => setSForm({ ...sForm, note: e.target.value })} /></div>
        </div>
      </Modal>
    </Layout>
  );
}
